/**
 * FAQ copy for the city × service index hubs and for the highest-demand
 * city pages. Kept out of the route/view components so the questions can be
 * edited without touching layout, and so both the visible accordion and the
 * FAQPage schema read from one source.
 */

export type Faq = { q: string; a: string };

/** /seo-services hub — questions buyers ask before picking a city page. */
export const HUB_FAQS_EN: Faq[] = [
  {
    q: "Are these city pages real, or the same page with the city name swapped?",
    a: "Each one is written against that market: the competitors actually bidding there, the neighborhoods and industries that generate searches, and the deliverables that make sense for local budgets. If a city page reads generic to you, tell me and I'll rewrite it — that's the whole point of keeping the index this size.",
  },
  {
    q: "Which service should I start with?",
    a: "If you have a physical location or serve a defined area, start with local SEO. If traffic is flat despite decent content, start with the technical audit. Stores with more than a few hundred SKUs usually need ecommerce SEO first, and brands already ranking well should look at AEO/GEO to hold ground in AI answers.",
  },
  {
    q: "My city isn't on the list — can you still help?",
    a: "Yes. The index covers the cities I get asked about most, but the work is remote either way. Pick the nearest city page to see the approach, then send me your actual market on the contact form and I'll quote it the same way.",
  },
  {
    q: "How much do these engagements cost?",
    a: "Technical audits are fixed-fee projects sized by page count. Local retainers for Bangladeshi businesses generally start around BDT 15,000–60,000 per month; ecommerce and international work sits higher. Full tiers are on the pricing page and every scope is confirmed in writing before work starts.",
  },
  {
    q: "Do you work in Bangla or English?",
    a: "Both. Calls, reports, and content can run in either language, and most Bangladeshi clients end up with a bilingual site plan because English carries the commercial keywords while Bangla wins long-tail and voice search. Every city page here has a Bangla counterpart where the market warrants it.",
  },
  {
    q: "How long before I see results from a city page's service?",
    a: "There is no fixed timeline. Local, organic and AI-answer visibility move at different speeds depending on your starting point, competition and how much work ships. Progress is measured monthly against a baseline recorded at the start, and no ranking date is promised. Anyone promising week-one results is selling something else.",
  },
];

/** /bn/seo-services hub. Written natively in Bangla, not translated. */
export const HUB_FAQS_BN: Faq[] = [
  {
    q: "এই শহরভিত্তিক পেজগুলো কি সত্যিই আলাদা, নাকি শুধু শহরের নাম বদলানো?",
    a: "প্রতিটি পেজ ঐ বাজার ধরে লেখা — কারা সেখানে প্রতিযোগিতা করছে, কোন এলাকা ও ইন্ডাস্ট্রি থেকে সার্চ আসে, আর স্থানীয় বাজেটে কোন কাজটা যুক্তিসঙ্গত। কোনো পেজ আপনার কাছে সাধারণ মনে হলে জানাবেন, আমি সেটি আবার লিখে দেব।",
  },
  {
    q: "কোন সেবা দিয়ে শুরু করা উচিত?",
    a: "দোকান বা নির্দিষ্ট এলাকার সেবা হলে লোকাল এসইও দিয়ে শুরু করুন। কনটেন্ট ভালো তবু ট্রাফিক বাড়ছে না — তাহলে টেকনিক্যাল অডিট। কয়েকশ প্রোডাক্টের অনলাইন স্টোর হলে ইকমার্স এসইও, আর যারা ইতিমধ্যে র‍্যাংক করছেন তাদের জন্য AEO/GEO।",
  },
  {
    q: "আমার শহর তালিকায় নেই — তাহলে কি কাজ হবে?",
    a: "হ্যাঁ। ইনডেক্সে সেই শহরগুলো আছে যেগুলো নিয়ে সবচেয়ে বেশি জিজ্ঞাসা আসে, কিন্তু কাজটা রিমোটেই হয়। কাছের শহরের পেজ দেখে পদ্ধতিটা বুঝুন, তারপর যোগাযোগ ফর্মে আপনার বাজারের কথা লিখুন — একইভাবে কোট দেব।",
  },
  {
    q: "খরচ কেমন পড়ে?",
    a: "টেকনিক্যাল অডিট নির্দিষ্ট ফি-র প্রজেক্ট, পেজ সংখ্যার ওপর নির্ভর করে। বাংলাদেশি ব্যবসার লোকাল রিটেইনার সাধারণত মাসে ১৫,০০০–৬০,০০০ টাকা থেকে শুরু; ইকমার্স ও আন্তর্জাতিক কাজ তার চেয়ে বেশি। কাজ শুরুর আগে পুরো স্কোপ লিখিতভাবে নিশ্চিত করা হয়।",
  },
  {
    q: "রিপোর্ট ও যোগাযোগ কোন ভাষায় হবে?",
    a: "আপনি যেটায় স্বচ্ছন্দ — বাংলা বা ইংরেজি, দুটোই চলে। বেশিরভাগ ক্লায়েন্টের সাইট প্ল্যান দ্বিভাষিক হয়: ইংরেজি বাণিজ্যিক কিওয়ার্ড ধরে, আর বাংলা লং-টেইল ও ভয়েস সার্চ জেতে।",
  },
  {
    q: "ফল দেখতে কত সময় লাগে?",
    a: "ম্যাপ প্যাক ও লোকাল ফলাফলে নড়াচড়া সাধারণত ৩০–৬০ দিনে দেখা যায়। প্রতিযোগিতামূলক বাণিজ্যিক কিওয়ার্ডে অর্গানিক র‍্যাংকিং ৩–৬ মাস নেয়, আর AI উত্তরে উল্লেখ তার এক-দুই মাস পরে আসে।",
  },
];

/**
 * Extra questions appended to the highest-demand city × service pages. These
 * are market-level (budget, competition, meeting logistics) rather than
 * service-level, so they layer on top of the per-service FAQ set.
 */
export const TOP_CITY_FAQS_EN: Record<string, Faq[]> = {
  dhaka: [
    {
      q: "Is SEO in Dhaka more expensive than the rest of Bangladesh?",
      a: "Usually yes, because the competition is deeper — you're often up against agencies with in-house content teams. Budgets that work in Rangpur may only fund half the content velocity needed in Gulshan or Motijheel. I'd rather scope a narrower set of Dhaka keywords properly than spread a small retainer across everything.",
    },
    {
      q: "Can we meet in person in Dhaka?",
      a: "Yes, for the kickoff and quarterly reviews. Day-to-day work runs over email and a monthly call, which keeps the retainer going into the work instead of travel.",
    },
  ],
  chittagong: [
    {
      q: "Do you understand Chattogram's import, shipping, and logistics market?",
      a: "That's the bulk of the Chattogram work I've done — freight forwarders, C&F agents, and importers. Those buyers search in a mix of English and transliterated Bangla, and the pages that win are the ones answering compliance and cost questions, not the ones stuffed with 'best shipping company' variants.",
    },
    {
      q: "Should a Chattogram business target Dhaka keywords too?",
      a: "Only after the home market is locked. National terms cost far more to win, and a Chattogram company that already ranks locally has the authority signals to make a Dhaka push realistic in phase two.",
    },
  ],
  rangpur: [
    {
      q: "You're based in Rangpur — does that mean cheaper rates?",
      a: "It means no agency overhead, so more of the budget goes into the actual work. Rangpur retainers are typically the lowest tier I run because the competitive bar is lower, not because the process is trimmed.",
    },
    {
      q: "Can we work face to face?",
      a: "Yes. I'm in Kamal Kachna and regularly meet Rangpur clients in person for kickoff, training sessions, and quarterly reviews.",
    },
  ],
  sylhet: [
    {
      q: "Do you handle Sylhet businesses targeting expat customers in the UK?",
      a: "Frequently — restaurants, travel, remittance, and property. That's a two-market setup: local Sylhet intent plus UK-based searches for Sylheti services, which need separate pages and their own hreflang setup rather than one page trying to serve both.",
    },
    {
      q: "Is Bangla or English better for Sylhet audiences?",
      a: "English usually pulls the diaspora and higher-value enquiries; Bangla pulls the local walk-in and phone traffic. Most Sylhet clients run both, with the split decided per service page rather than site-wide.",
    },
  ],
  khulna: [
    {
      q: "Is there enough search volume in Khulna to justify SEO?",
      a: "For most categories, yes — but the honest framing is that Khulna volumes are smaller and cheaper to win. A page ranking for a few hundred monthly searches with clear buying intent beats chasing a national term you'll never reach.",
    },
    {
      q: "Which Khulna industries do you see the best results in?",
      a: "Shrimp and seafood export, healthcare, education, and construction supply. Those buyers research before calling, which is exactly the behaviour SEO captures.",
    },
  ],
};

/** Bangla equivalents for the cities that have `/bn` city pages. */
export const TOP_CITY_FAQS_BN: Record<string, Faq[]> = {
  tangail: [
    {
      q: "টাঙ্গাইলের ছোট ব্যবসার জন্য এসইও কি লাভজনক?",
      a: "সার্চ ভলিউম ঢাকার চেয়ে কম, তবে প্রতিযোগিতাও কম — তাই খরচ কম রেখেই প্রথম পাতায় ওঠা সম্ভব। যেসব সেবায় মানুষ ফোন করার আগে খোঁজে (ক্লিনিক, কোচিং, তাঁত ও শাড়ি ব্যবসা), সেখানে ফল সবচেয়ে ভালো।",
    },
    {
      q: "কাজটা কি বাংলায় হবে?",
      a: "হ্যাঁ। রিপোর্ট, কনটেন্ট প্ল্যান ও মাসিক কল বাংলাতেই হয়; শুধু কিওয়ার্ড ও প্রযুক্তিগত টার্মগুলো ইংরেজিতে থাকে যাতে ডেভেলপার বুঝতে পারে।",
    },
  ],
  feni: [
    {
      q: "ফেনীতে প্রবাসী গ্রাহকদের টার্গেট করা যায়?",
      a: "যায়, এবং এটা ফেনীর অনেক ব্যবসার জন্য সবচেয়ে বড় সুযোগ — রেমিট্যান্স, জমি ও নির্মাণসেবা, ট্রাভেল। এর জন্য আলাদা পেজ দরকার হয়, কারণ প্রবাসী সার্চ ইংরেজিতে হয় আর স্থানীয় সার্চ বাংলায়।",
    },
    {
      q: "কত দিনে ফল দেখা যাবে?",
      a: "গুগল বিজনেস প্রোফাইল ঠিক করার পর ম্যাপে পরিবর্তন ৩০–৬০ দিনে আসে। ওয়েবসাইটের অর্গানিক র‍্যাংকিং ৩–৬ মাস সময় নেয়।",
    },
  ],
  noakhali: [
    {
      q: "নোয়াখালীর ব্যবসার জন্য কোন সেবা আগে দরকার?",
      a: "বেশিরভাগ ক্ষেত্রে লোকাল এসইও — গুগল বিজনেস প্রোফাইল, রিভিউ ও সাইটেশন ঠিক করলেই প্রথম ফলাফল আসে। ওয়েবসাইট বড় হলে তার পরে টেকনিক্যাল অডিট।",
    },
    {
      q: "সরাসরি দেখা করা যাবে?",
      a: "কাজ রিমোটে চলে, মাসে একটি কল হয়। প্রয়োজনে ভিডিও কলে স্ক্রিন শেয়ার করে পুরো রিপোর্ট ধরে ধরে বুঝিয়ে দিই।",
    },
  ],
  kushtia: [
    {
      q: "কুষ্টিয়ায় প্রতিযোগিতা কেমন?",
      a: "বেশিরভাগ ক্যাটাগরিতে প্রতিযোগীদের সাইট এখনো দুর্বল — বেসিক টেকনিক্যাল কাজ আর নিয়মিত কনটেন্ট দিয়েই এগিয়ে যাওয়া যায়। শিক্ষা ও স্বাস্থ্যসেবায় তুলনামূলক চাপ বেশি।",
    },
    {
      q: "বাজেট কত রাখতে হবে?",
      a: "কুষ্টিয়ার বেশিরভাগ ব্যবসার জন্য মাসিক ১৫,০০০–৩৫,০০০ টাকার রিটেইনারেই কাজ শুরু করা যায়। কাজ শুরুর আগে স্কোপ লিখিতভাবে দেওয়া হয়।",
    },
  ],
  nilphamari: [
    {
      q: "নীলফামারী ও সৈয়দপুর — দুটোতেই র‍্যাংক করা যাবে?",
      a: "হ্যাঁ, তবে আলাদা পেজ লাগে। এক পেজে দুই শহরের নাম ঢোকালে কোনোটিতেই ভালো ফল আসে না; প্রতিটি এলাকার জন্য আলাদা কনটেন্ট ও প্রোফাইল সিগন্যাল দরকার।",
    },
    {
      q: "ছোট শহরে এসইও করে কি যথেষ্ট গ্রাহক আসে?",
      a: "ভলিউম কম, কিন্তু উদ্দেশ্য পরিষ্কার — যারা খোঁজে তারা কিনতে চায়। মাসে অল্প কিছু সার্চ থেকেও ধারাবাহিক অর্ডার আসে, বিশেষ করে সেবা ও স্বাস্থ্য খাতে।",
    },
  ],
};
